/**
 * NotificationFeed — akıllı konteyner. Veri çekme (useNotifications/useMarkRead/
 * useMarkAllRead) + filtre state + bucket gruplama + roving-tabindex ok-tuşu
 * navigasyonu + aria-live tek burada yaşar. İki density modunda render edilir:
 *   - density="compact"     → panel (NotificationBell popover/sheet içeriği)
 *   - density="comfortable" → tam sayfa (/bildirimler)
 *
 * Props:
 *   density         : 'compact' | 'comfortable' (default: 'comfortable')
 *   onClose         : panel kapatma callback'i (sadece density="compact" anlamlı)
 *   showViewAllLink : "Tümünü Gör" alt linkini göster (sadece panelde true)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCheck, Loader2 } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import { useNotifications, useMarkRead, useMarkAllRead } from '../../hooks/cabin/useNotifications';
import { interpolate } from '../../utils/cabinI18n';
import NotificationRow from './NotificationRow';
import BucketHeader from './BucketHeader';
import NotificationFilterTabs from './NotificationFilterTabs';
import NotificationEmptyState from './NotificationEmptyState';
import NotificationErrorState from './NotificationErrorState';
import NotificationSkeletonList from './NotificationSkeletonList';
import { groupByBucket, filterNotifications } from './notificationGrouping';

export default function NotificationFeed({ density = 'comfortable', onClose, showViewAllLink = false }) {
  const { t, lang } = useLanguage();
  const toast = useToast();
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useNotifications();
  const markReadMutation = useMarkRead();
  const markAllMutation = useMarkAllRead();

  const [filter, setFilter] = useState('all');
  const itemRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const allNotifications = useMemo(() => data?.notifications || [], [data]);
  const unreadCount = data?.unread_count || 0;

  const filteredNotifications = useMemo(
    () => filterNotifications(allNotifications, filter),
    [filter, allNotifications]
  );

  const buckets = useMemo(() => groupByBucket(filteredNotifications), [filteredNotifications]);

  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, filteredNotifications.length);
    setActiveIndex((idx) => Math.min(idx, Math.max(0, filteredNotifications.length - 1)));
  }, [filteredNotifications.length]);

  const handleMarkRead = useCallback(
    (id) => {
      markReadMutation.mutate(id);
    },
    [markReadMutation]
  );

  const handleMarkAll = useCallback(() => {
    markAllMutation.mutate(undefined, {
      onError: () => {
        toast.error(t('cabin.notifications.markAllError'));
      },
    });
  }, [markAllMutation, toast, t]);

  const handleRowActivate = useCallback(() => {
    if (density === 'compact' && onClose) onClose();
  }, [density, onClose]);

  const handleKeyDown = useCallback(
    (e) => {
      const max = filteredNotifications.length - 1;
      if (max < 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((idx) => {
          const next = Math.min(idx + 1, max);
          itemRefs.current[next]?.focus();
          return next;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((idx) => {
          const prev = Math.max(idx - 1, 0);
          itemRefs.current[prev]?.focus();
          return prev;
        });
      } else if (e.key === 'Home') {
        e.preventDefault();
        setActiveIndex(0);
        itemRefs.current[0]?.focus();
      } else if (e.key === 'End') {
        e.preventDefault();
        setActiveIndex(max);
        itemRefs.current[max]?.focus();
      }
    },
    [filteredNotifications.length]
  );

  const handleViewAll = useCallback(() => {
    if (onClose) onClose();
    navigate('/bildirimler');
  }, [onClose, navigate]);

  const bucketLabels = {
    today: t('cabin.notifications.bucket.today'),
    this_week: t('cabin.notifications.bucket.this_week'),
    week: t('cabin.notifications.bucket.this_week'),
    older: t('cabin.notifications.bucket.older'),
  };

  const isCompact = density === 'compact';
  const titleClass = isCompact
    ? 'text-sm font-black text-[var(--text-main)]'
    : 'text-2xl font-bold text-[var(--text-main)]';

  // sr-only özet duyuru: sadece density="comfortable" (tam sayfa) için —
  // density="compact" panelinde bu zaten NotificationBell'de kendi liveRegion'ı
  // ile karşılanıyor (bkz. NotificationBell.jsx). Liste DOM'unun kendisi
  // aria-live TAŞIMAZ — aksi halde skeleton→veri geçişinde, filtre değişiminde
  // ve 60sn poll'de tüm bucket başlıkları + bildirim satırları ekran okuyucuya
  // otomatik okunur (a11y regresyonu).
  const liveMessage =
    unreadCount > 0
      ? interpolate(t('cabin.notifications.sr_live_unread'), { count: unreadCount })
      : t('cabin.notifications.sr_live_all_read');

  let flatIndex = -1;

  // isCompact kök: `h-full` (percentage height) bu bağlamda GÜVENİLMEZ —
  // ölçümle doğrulandı (bkz. proje raporu "BUG A" turu): ebeveyn (NotificationBell
  // Popover wrapper'ı) flex-grow ile 476px'e definite olarak boyutlanmış olsa
  // bile, height:100% iki seviye derinlikte percentage-height zincirini
  // güvenilir çözmüyor (Chromium'da da). flex-1 min-h-0 (flex-grow tabanlı,
  // percentage'a dayanmayan) zaten çalıştığı kanıtlanmış tek desen — scroll
  // konteynerinde ve NotificationBell wrapper'ında aynı desen kullanılıyor.
  return (
    <div className={isCompact ? 'flex flex-col flex-1 min-h-0' : 'flex flex-col'}>
      {/* Header */}
      <div
        className={
          isCompact
            ? 'flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0'
            : 'flex items-center justify-between mb-1'
        }
      >
        <h2 className={titleClass}>
          {t(isCompact ? 'cabin.notifications.title' : 'cabin.notifications.page.title')}
          {isCompact && unreadCount > 0 && (
            <span className="ml-2 px-1.5 py-0.5 bg-rose-600 text-white text-[10px] font-black rounded-full">
              {unreadCount}
            </span>
          )}
        </h2>
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={handleMarkAll}
            disabled={markAllMutation.isPending}
            className="flex items-center gap-1.5 text-xs font-semibold text-[var(--brand-primary)] hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] rounded disabled:opacity-50 min-h-[44px] px-1"
          >
            {markAllMutation.isPending ? (
              <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            ) : (
              <CheckCheck size={14} aria-hidden="true" />
            )}
            <span className={isCompact ? 'hidden sm:inline' : 'inline'}>
              {t('cabin.notifications.mark_all_read')}
            </span>
          </button>
        )}
      </div>

      {!isCompact && (
        <p className="text-sm text-[var(--text-secondary)] mb-4">
          {t('cabin.notifications.page.subtitle')}
        </p>
      )}

      <NotificationFilterTabs
        filter={filter}
        onChange={setFilter}
        allCount={allNotifications.length}
        unreadCount={unreadCount}
      />

      {!isCompact && (
        <span className="sr-only" aria-live="polite" aria-atomic="true">
          {liveMessage}
        </span>
      )}

      <div
        role="region"
        aria-label={t('cabin.notifications.list_aria_label')}
        onKeyDown={handleKeyDown}
        className={isCompact ? 'flex-1 min-h-0 overflow-y-auto' : 'flex flex-col mt-2'}
      >
        {isLoading && <NotificationSkeletonList />}

        {isError && !isLoading && <NotificationErrorState onRetry={() => refetch()} />}

        {!isLoading && !isError && filteredNotifications.length === 0 && (
          <NotificationEmptyState filtered={filter === 'unread'} />
        )}

        {!isLoading && !isError && buckets.length > 0 && (
          <div className="pb-3 flex flex-col gap-2">
            {buckets.map(({ bucket, items }) => {
              const headerId = `notif-bucket-${density}-${bucket}`;
              return (
                <section key={bucket} aria-labelledby={headerId}>
                  <BucketHeader label={bucketLabels[bucket] || bucket} lang={lang} headerId={headerId} />
                  <div className="flex flex-col gap-1.5 px-1">
                    {items.map((notif) => {
                      flatIndex += 1;
                      const idx = flatIndex;
                      return (
                        <NotificationRow
                          key={notif.notification_id}
                          notif={notif}
                          onMarkRead={handleMarkRead}
                          onActivate={handleRowActivate}
                          rowRef={(el) => {
                            itemRefs.current[idx] = el;
                          }}
                          tabIndex={idx === activeIndex ? 0 : -1}
                          onFocusRow={() => setActiveIndex(idx)}
                        />
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>

      {showViewAllLink && (
        <div className="shrink-0 border-t-2 border-gray-100 p-2">
          <button
            type="button"
            onClick={handleViewAll}
            className="w-full flex items-center justify-center gap-1.5 py-2.5 min-h-[44px] text-sm font-bold text-[var(--brand-primary)] hover:bg-gray-50 rounded-xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
          >
            {t('cabin.notifications.view_all')}
          </button>
        </div>
      )}
    </div>
  );
}
